"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState, useRef } from "react";
import dynamic from "next/dynamic";
import { Chip, SelectedMap, FilterConfig } from "./Types";
import { FILTER_KEYS } from "./Carfilter";
import CommonFilter from "./CommonFilter";
import Image from "next/image";
import { DEFAULT_SORT_OPTION } from "@/app/utilities/constants";

const MobileSortSheet = dynamic(() => import("./Mobilesortsheet"));
const MobileFilterSheet = dynamic(() => import("./Mobilefiltersheet"));

const XIcon: React.FC<{ size?: number }> = ({ size = 12 }) => <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                alt="cross icon"
                title="cross icon"
                width={size}
                height={size}
            />;

const FilterIcon: React.FC = () => <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}fillter-icon.svg`}
                alt="fillter icon"
                title="fillter icon"
                width={16}
                height={16}
            />;

const SortIcon: React.FC = () => <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}sort-fillter.svg`}
                alt="cross icon"
                title="sort-fillter icon"
                width={16}
                height={16}
            />;

// ─── Mobile section labels (for chips coming from mobile-only filters) ────────
const MOBILE_SECTION_LABELS: Record<string, string> = {
    price: "Price",
    brand: "Brand",
    body_type: "Body Type",
    seating: "Seating",
    range: "Range",
    battery: "Battery",
    charging: "Charging",
};

// ─── Build chip list from selectedMap and filterConfigs ───────────────────────
function buildChips(
    selectedMap: SelectedMap,
    filterConfigs: FilterConfig[],
): Chip[] {
    const chips: Chip[] = [];

    Object.entries(selectedMap).forEach(([filterKey, values]) => {
        if (!values || values.length === 0) return;

        // Try desktop filterConfigs first
        const desktopFilter = filterConfigs.find((f) => f.key === filterKey);
        if (desktopFilter) {
            values.forEach((val) => {
                const opt = desktopFilter.options.find((o) => o.value === val);
                if (opt) {
                    chips.push({
                        filterKey,
                        filterTitle: desktopFilter.title,
                        value: val,
                        label: opt.label,
                    });
                }
            });
            return;
        }

        // Fallback: mobile-only sections (range, battery, charging, seating, etc.)
        // Use the raw value as label but capitalise it nicely
        const sectionTitle =
            MOBILE_SECTION_LABELS[filterKey] ??
            filterKey.charAt(0).toUpperCase() + filterKey.slice(1);

        values.forEach((val) => {
            chips.push({
                filterKey,
                filterTitle: sectionTitle,
                value: val,
                label: val, // raw value — MobileFilterSheet can export labels if needed
            });
        });
    });

    return chips;
}

// ─── Component Props ──────────────────────────────────────────────────────────
export interface CarFilterClientProps {
    filterConfigs: FilterConfig[];
    defaultFilters?: Record<string, string[] | string | boolean>;
    stickyOnScroll?: boolean;
    clearAllTrackingClass?: string;
    applyFilterTrackingClass?: string;
    sortAppliedTrackingClass?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CarFilterClient({
    filterConfigs,
    defaultFilters,
    stickyOnScroll = false,
    clearAllTrackingClass = '',
    applyFilterTrackingClass = '',
    sortAppliedTrackingClass = ''
}: CarFilterClientProps) {
    const router = useRouter();
    const searchParams = useSearchParams();

    const isLatestPage = defaultFilters?.is_latest || false
    const defaultSortVal = isLatestPage ? "launch_date_desc" : DEFAULT_SORT_OPTION

    const [localSelectedMap, setLocalSelectedMap] = useState<SelectedMap>({});
    const [localSortValue, setLocalSortValue] = useState<string>(defaultSortVal);
    const [showMobileBar, setShowMobileBar] = useState<boolean>(!stickyOnScroll);
    const lastScrollY = useRef<number>(0);
    const pendingScroll = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync local state with URL query params
    useEffect(() => {
        const map: SelectedMap = {};
        searchParams?.forEach((value, key) => {
            if (key !== "sort" && value) {
                map[key] = value.split(",");
            }
        });
        setLocalSelectedMap(map);
        const sort = searchParams?.get("sort") || defaultSortVal;
        setLocalSortValue(sort);

        if (pendingScroll.current) {
            if (containerRef.current) {
                const elementPosition = containerRef.current.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.scrollY - 80;
                window.scrollTo({ top: offsetPosition, behavior: "smooth" });
            } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }
            pendingScroll.current = false;
        }
    }, [searchParams]);

    // ── Scroll handler for mobile bar visibility ──────────────────────────────
    useEffect(() => {
        if (!stickyOnScroll) return;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            const hasChips = Object.values(localSelectedMap).some(arr => arr && arr.length > 0);
            const threshold = hasChips ? 0 : 390;

            if (currentScrollY > threshold) {
                setShowMobileBar(true);
            } else {
                setShowMobileBar(false);
            }

            lastScrollY.current = currentScrollY;
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [stickyOnScroll, localSelectedMap]);

    const updateQueryParams = (
        newFilters: Record<string, string[] | string>,
        newSort: string,
    ) => {
        const query = new URLSearchParams();

        // Add filters to query
        Object.entries(newFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value?.length) {
                query.set(key, value?.join(","));
            } else if (!Array.isArray(value) && !!value) {
                query.set(key, value.toString());
            }
        });

        // Add sort if present
        if (newSort) query.set("sort", newSort);

        // Update URL without reloading
        router?.replace(`?${query.toString()}`, { scroll: false });
    };

    const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
    const [mobileSortOpen, setMobileSortOpen] = useState<boolean>(false);

    // ── Select / deselect ──────────────────────────────────────────────────────
    const handleSelect = (
        filterKey: string,
        value: string,
        type: "single" | "multi",
    ) => {
        const current = localSelectedMap[filterKey] ?? [];
        let updated: string[];

        if (type === "single") {
            updated = current[0] === value ? [] : [value];
        } else {
            updated = current.includes(value)
                ? current.filter((v) => v !== value)
                : [...current, value];
        }

        const newMap = { ...localSelectedMap, [filterKey]: updated };
        setLocalSelectedMap(newMap);
        updateQueryParams(newMap, localSortValue);
    };

    // ── Remove one chip ────────────────────────────────────────────────────────
    const handleRemoveChip = (chip: Chip) => {
        const current = localSelectedMap[chip.filterKey] ?? [];
        const updated = current.filter((v) => v !== chip.value);

        const newMap = { ...localSelectedMap, [chip.filterKey]: updated };
        setLocalSelectedMap(newMap);
        updateQueryParams(newMap, localSortValue);
    };

    // ── Clear all ──────────────────────────────────────────────────────────────
    const handleClearAll = () => {
        setLocalSelectedMap({});
        updateQueryParams({}, localSortValue);
    };

    // ── Apply (close mobile sheet) ─────────────────────────────────────────────
    const handleApplyFilterSheet = (newMap: SelectedMap) => {
        setLocalSelectedMap(newMap);
        updateQueryParams(newMap, localSortValue);
        setMobileFilterOpen(false);
        pendingScroll.current = true;
    };

    const fuelVal = defaultFilters?.[FILTER_KEYS.FUEL_TYPE];
    const defaultFuels = Array.isArray(fuelVal) ? fuelVal : (typeof fuelVal === "string" ? [fuelVal] : []);
    const isElectricSelected = localSelectedMap[FILTER_KEYS.FUEL_TYPE]?.some((f: any) => typeof f === "string" && f.toLowerCase() === "electric") || defaultFuels.some((f: any) => typeof f === "string" && f.toLowerCase() === "electric");
    const isNonElectricSelected = localSelectedMap[FILTER_KEYS.FUEL_TYPE]?.some((f: any) => typeof f === "string" && f.toLowerCase() !== "electric") || defaultFuels.some((f: any) => typeof f === "string" && f.toLowerCase() !== "electric");

    // ── Side Effects for conflicting filters ──────────────────────────────────
    // update query params when fuel type is changed
    useEffect(() => {
        let hasChanged = false;
        const newMap = { ...localSelectedMap };

        // if both are selected then empty range and mileage
        if (isElectricSelected && isNonElectricSelected) {
            newMap[FILTER_KEYS.MILEAGE] = [];
            newMap[FILTER_KEYS.RANGE] = [];
            newMap[FILTER_KEYS.BATTERY_CAPACITY] = [];
            hasChanged = true;
        }

        if (isElectricSelected) {
            newMap[FILTER_KEYS.TRANSMISSION] = [];
        }

        if (isElectricSelected && localSelectedMap[FILTER_KEYS.MILEAGE]?.length > 0) {
            newMap[FILTER_KEYS.MILEAGE] = [];
            hasChanged = true;
        }

        if (!isElectricSelected && localSelectedMap[FILTER_KEYS.RANGE]?.length > 0) {
            newMap[FILTER_KEYS.RANGE] = [];
            hasChanged = true;
        }

        if (!isElectricSelected && localSelectedMap[FILTER_KEYS.BATTERY_CAPACITY]?.length > 0) {
            newMap[FILTER_KEYS.BATTERY_CAPACITY] = [];
            hasChanged = true;
        }

        if (hasChanged) {
            setLocalSelectedMap(newMap);
            updateQueryParams(newMap, localSortValue);
        }
    }, [localSelectedMap[FILTER_KEYS.FUEL_TYPE], isElectricSelected, isNonElectricSelected]);

    const chips: Chip[] = useMemo(() => buildChips(localSelectedMap, filterConfigs), [localSelectedMap, filterConfigs]);
    const hasChips = chips.length > 0;

    // update filter configs based on selected filters
    const updatedFilterConfigs = useMemo(() => {

        return filterConfigs
            .map((filter) => {
                if (isElectricSelected && isNonElectricSelected && (filter.key === FILTER_KEYS.MILEAGE || filter.key === FILTER_KEYS.RANGE || filter.key === FILTER_KEYS.BATTERY_CAPACITY)) {
                    return {
                        ...filter,
                        options: [],
                    };
                }

                if (isElectricSelected) {
                    if (filter.key === FILTER_KEYS.TRANSMISSION) {
                        return {
                            ...filter,
                            options: [],
                        };
                    }
                }

                if (isElectricSelected && filter.key === FILTER_KEYS.MILEAGE) {
                    return {
                        ...filter,
                        options: [],
                    };
                }

                if (!isElectricSelected && filter.key === FILTER_KEYS.RANGE) {
                    return {
                        ...filter,
                        options: [],
                    };
                }

                if (!isElectricSelected && filter.key === FILTER_KEYS.BATTERY_CAPACITY) {
                    return {
                        ...filter,
                        options: [],
                    };
                }
                return filter;
            })
            .filter((f: any) => f !== null);
    }, [filterConfigs, isElectricSelected, isNonElectricSelected]);

    return (
        <div ref={containerRef}>
            <div className="max-[640px]:!hidden">
                <CommonFilter
                    selectedMap={localSelectedMap}
                    onSelect={handleSelect}
                    sortValue={localSortValue}
                    onSortChange={(value) => {
                        setLocalSortValue(value);
                        updateQueryParams(localSelectedMap, value);
                    }}
                    filterConfigs={updatedFilterConfigs}
                    applyFilterTrackingClass={applyFilterTrackingClass}
                    sortAppliedTrackingClass={sortAppliedTrackingClass}
                    is_latest={isLatestPage as boolean}
                />

                {/* Desktop chips row */}
                {hasChips && (
                    <div className="mt-2.5">
                        <div className="flex items-center gap-2 flex-wrap py-0.5">
                            <div className="flex gap-2 flex-wrap items-center">
                                {chips.map((chip) => (
                                    <span
                                        key={`${chip.filterKey}-${chip.value}`}
                                        className="flex items-center gap-[5px] py-1 px-3 rounded-[50px] text-[12px] leading-5 font-medium bg-[#F3F4F6] text-[#4A5565] border border-[#E2E8F0] whitespace-nowrap"
                                    >
                                        {chip.label}
                                        <button
                                            className="bg-transparent border-none cursor-pointer opacity-60 flex items-center p-0 transition-[opacity] duration-180 ease-[ease] text-[#1E2939] hover:opacity-100"
                                            onClick={() =>
                                                handleRemoveChip(chip)
                                            }
                                            type="button"
                                            aria-label={`Remove ${chip.label}`}
                                        >
                                            <Image
                                                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                                                alt="cross icon"
                                                title="cross icon"
                                                width={12}
                                                height={12}
                                            />
                                        </button>
                                    </span>
                                ))}
                            </div>

                            <button
                                className={`bg-transparent border-none text-[#ef4444] text-[13px] font-semibold cursor-pointer py-1 px-0.5 whitespace-nowrap ${clearAllTrackingClass}`}
                                onClick={() => handleClearAll()}
                                type="button"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {hasChips && (
                <div
                    className="hidden items-center gap-2 py-2.5 overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:none] bg-white [&::-webkit-scrollbar]:hidden max-[640px]:flex min-[641px]:!hidden"
                >
                    {chips.map((chip) => (
                        <span
                            key={`m-${chip.filterKey}-${chip.value}`}
                            className="inline-flex items-center gap-1.5 py-1.5 px-3 rounded-[50px] text-[13px] font-medium bg-[#f5f6f8] text-[#1a1d23] border border-[#e2e5ea] whitespace-nowrap shrink-0"
                        >
                            {chip.label}
                            <button
                                className="bg-transparent border-none cursor-pointer opacity-55 flex items-center p-0 text-[#1a1d23] transition-[opacity] duration-150 ease-[ease] hover:opacity-100"
                                onClick={() => handleRemoveChip(chip)}
                                type="button"
                                aria-label={`Remove ${chip.label}`}
                            >
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                                    alt="cross icon"
                                    title="cross icon"
                                    width={11}
                                    height={11}
                                />
                            </button>
                        </span>
                    ))}

                    {/* Clear All — at the end of the scroll row */}
                    <button
                        className={`text-[#ef4444] cursor-pointer whitespace-nowrap border-none shrink-0 text-[13px] font-semibold sticky right-0 z-[9] bg-transparent bg-[linear-gradient(90deg,rgba(255,255,255,0.1)_0%,#fff_30%)] py-2 pr-2.5 pl-5 ${clearAllTrackingClass}`}
                        onClick={() => handleClearAll()}
                        type="button"
                    >
                        Clear All
                    </button>
                </div>
            )}

            {/* ── Mobile Sort / Filter bar ─────────────────── */}
            <div className="relative">
                <div
                    className="hidden gap-2.5 py-2 px-4 bg-[#FDFDFD] fixed left-1/2 [transform:translateX(-50%)] bottom-[50px] w-80 z-[99] border-[0.4px] border-[#00000033] rounded-[50px] max-[640px]:flex min-[641px]:!hidden"
                    style={{
                        opacity: showMobileBar ? 1 : 0,
                        visibility: showMobileBar ? 'visible' : 'hidden',
                        transition: 'opacity 0.3s ease, visibility 0.3s ease'
                    }}
                >
                    <button
                        className="flex-1 flex items-center justify-center gap-2 rounded-[10px] bg-white text-[14px] font-semibold text-[#374151] cursor-pointer transition-[background] duration-150 ease-[ease] hover:bg-[#f9fafb]"
                        onClick={() => setMobileSortOpen(true)}
                        type="button"
                    >
                        <SortIcon />
                        Sort
                    </button>
                    <span className="max-[640px]:w-px max-[640px]:h-[30px] max-[640px]:bg-[#D1D5DC]"></span>
                    <button
                        className="flex-1 flex items-center justify-center gap-2 rounded-[10px] bg-white text-[14px] font-semibold text-[#374151] cursor-pointer transition-[background] duration-150 ease-[ease] hover:bg-[#f9fafb]"
                        onClick={() => setMobileFilterOpen(true)}
                        type="button"
                    >
                        <FilterIcon />
                        Filter
                        {hasChips && (
                            <span className="bg-[#008C8B] text-white rounded-full w-5 h-5 flex items-center justify-center text-[12px] font-bold shrink-0">
                                {chips.length}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Bottom sheets ────────────────────────────── */}
            <MobileFilterSheet
                open={mobileFilterOpen}
                onClose={() => setMobileFilterOpen(false)}
                selectedMap={localSelectedMap}
                onApply={handleApplyFilterSheet}
                filterConfigs={updatedFilterConfigs}
                clearAllTrackingClass={clearAllTrackingClass}
                applyFilterTrackingClass={applyFilterTrackingClass}
            />

            <MobileSortSheet
                open={mobileSortOpen}
                onClose={() => setMobileSortOpen(false)}
                sortValue={localSortValue}
                onSortChange={(value) => {
                    setLocalSortValue(value);
                    updateQueryParams(localSelectedMap, value);
                    pendingScroll.current = true;
                }}
                sortAppliedTrackingClass={sortAppliedTrackingClass}
                is_latest={isLatestPage as boolean}
            />
        </div>
    );
}
